import React, { useState } from 'react';
import dayjs from 'dayjs';
import { Box, Paper, ButtonGroup, Button, Typography, Stack } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

const getTimeFilterOptions = () => {
  const today = dayjs();
  const currentQuarter = Math.floor(today.month() / 3);
  const quarterStartMonth = currentQuarter * 3;
  const quarterStart = today.month(quarterStartMonth).startOf('month');
  const quarterEnd = today.month(quarterStartMonth + 2).endOf('month');

  return [
    {
      id: 'Today',
      label: 'Today',
      dateRange: today.format('DD/MM/YY')
    },
    {
      id: 'This Week',
      label: 'This Week',
      dateRange: `${today.startOf('week').format('DD/MM/YY')}-${today.endOf('week').format('DD/MM/YY')}`
    },
    {
      id: 'This Month',
      label: 'This Month',
      dateRange: `${today.startOf('month').format('DD/MM/YY')}-${today.endOf('month').format('DD/MM/YY')}`
    },
    {
      id: 'This Quarter',
      label: 'This Quarter',
      dateRange: `${quarterStart.format('DD/MM/YY')}-${quarterEnd.format('DD/MM/YY')}`
    },
    {
      id: 'Custom',
      label: 'Custom',
      dateRange: ''
    }
  ];
};

function getDateRanges(filter) {
  const today = dayjs().endOf('day');
  let currentStart, currentEnd, compareStart, compareEnd, compareLabel;
  const filterName = filter.id || filter;

  switch (filterName) {
    case 'Today':
      currentEnd = today;
      currentStart = today.startOf('day');
      compareEnd = currentStart.subtract(1, 'day');
      compareStart = compareEnd.startOf('day');
      compareLabel = 'yesterday';
      break;
    case 'This Week':
      currentEnd = today;
      currentStart = today.startOf('week');
      compareEnd = currentStart.subtract(1, 'day');
      compareStart = compareEnd.startOf('week');
      compareLabel = 'last week';
      break;
    case 'This Month':
      currentEnd = today;
      currentStart = today.startOf('month');
      compareEnd = currentStart.subtract(1, 'day');
      compareStart = compareEnd.startOf('month');
      compareLabel = 'last month';
      break;
    case 'This Quarter':
      const currentQuarter = Math.floor(today.month() / 3);
      const quarterStartMonth = currentQuarter * 3;
      currentStart = today.month(quarterStartMonth).startOf('month');
      currentEnd = today.month(quarterStartMonth + 2).endOf('month');
      const prevQuarterEnd = currentStart.subtract(1, 'day');
      const prevQuarterStartMonth = Math.floor(prevQuarterEnd.month() / 3) * 3;
      compareStart = prevQuarterEnd.month(prevQuarterStartMonth).startOf('month');
      compareEnd = prevQuarterEnd;
      compareLabel = 'last quarter';
      break;
    case 'Custom':
    default:
      currentEnd = today;
      currentStart = today.subtract(7, 'day');
      compareEnd = currentStart;
      compareStart = currentStart.subtract(7, 'day');
      compareLabel = 'previous period';
  }
  return { currentStart, currentEnd, compareStart, compareEnd, compareLabel };
}

const DateRangeFilter = ({ onChange }) => {
  const timeFilterOptions = getTimeFilterOptions();
  const [selectedTimeFilter, setSelectedTimeFilter] = useState(timeFilterOptions[0]);
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  const [customApplied, setCustomApplied] = useState(false);
  const [customDateRange, setCustomDateRange] = useState('');

  const handleTimeFilterChange = (option) => {
    console.log('DateRangeFilter: handleTimeFilterChange called with option:', option);
    setSelectedTimeFilter(option);
    setCustomApplied(false);

    if (option.id !== 'Custom') {
      const ranges = getDateRanges(option);
      if (onChange) {
        onChange({
          filter: option,
          ...ranges
        });
      }
    }
  };

  const handleApplyCustom = () => {
    if (customStart && customEnd && !customStart.isAfter(customEnd)) {
      const dateRangeText = `${customStart.format('DD/MM/YY')}-${customEnd.format('DD/MM/YY')}`;
      setCustomDateRange(dateRangeText);
      setCustomApplied(true);

      const customOption = {
        id: 'Custom',
        label: 'Custom',
        dateRange: dateRangeText
      };

      setSelectedTimeFilter(customOption);

      if (onChange) {
        const diff = customEnd.diff(customStart, 'day') + 1;
        const compareEnd = customStart.subtract(1, 'day');
        const compareStart = compareEnd.subtract(diff - 1, 'day');
        onChange({
          filter: customOption,
          currentStart: customStart,
          currentEnd: customEnd,
          compareStart,
          compareEnd,
          compareLabel: 'previous period'
        });
      }
    }
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Paper sx={{ display: 'inline-flex', p: 0.5, borderRadius: 2 }}>
        <ButtonGroup variant="text" aria-label="time filter button group">
          {timeFilterOptions.map((option) => (
            <Button
              key={option.id}
              onClick={() => handleTimeFilterChange(option)}
              sx={{
                px: 2,
                py: 1,
                backgroundColor:
                  selectedTimeFilter.id === option.id ? 'rgba(52, 152, 219, 0.08)' : 'transparent',
                color: selectedTimeFilter.id === option.id ? 'primary.main' : 'text.secondary',
                fontWeight: selectedTimeFilter.id === option.id ? 600 : 500,
                '&:hover': { backgroundColor: 'rgba(52, 152, 219, 0.04)' }
              }}
            >
              <Stack alignItems="center" spacing={0.5}>
                <Typography variant="body2" sx={{ fontWeight: 'inherit' }}>
                  {option.label}
                </Typography>
                {option.id !== 'Custom' || (option.id === 'Custom' && customDateRange) ? (
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.7rem',
                      opacity: 0.8,
                      lineHeight: 1
                    }}
                  >
                    {option.id === 'Custom' ? customDateRange : option.dateRange}
                  </Typography>
                ) : null}
              </Stack>
            </Button>
          ))}
        </ButtonGroup>

        {selectedTimeFilter.id === 'Custom' && (
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 2, gap: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Start Date"
                value={customStart}
                onChange={setCustomStart}
                maxDate={customEnd || undefined}
                slotProps={{ textField: { size: 'small', sx: { minWidth: 120 } } }}
                format="DD/MM/YY"
              />
              <DatePicker
                label="End Date"
                value={customEnd}
                onChange={setCustomEnd}
                minDate={customStart || undefined}
                slotProps={{ textField: { size: 'small', sx: { minWidth: 120 } } }}
                format="DD/MM/YY"
              />
            </LocalizationProvider>
            <Button
              variant="contained"
              size="small"
              sx={{ ml: 1 }}
              disabled={!customStart || !customEnd || customStart.isAfter(customEnd)}
              onClick={handleApplyCustom}
            >
              Apply
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default DateRangeFilter;
